import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import Tour, { resetTour } from "../components/Tour";
import { formatTrialDuration } from "../lib/trialDuration";
import * as Phosphor from "@phosphor-icons/react";
import {
  Buildings,
  ChartPieSlice,
  Scales,
  Calculator,
  Sparkle,
  Crown,
  FilePdf,
  ShareNetwork,
  CaretDown,
  PlayCircle,
  Question,
  MapTrifold,
  Scroll,
  UserCircle,
  ShieldCheck,
  MapPin,
  EnvelopeSimple,
  Gift,
  Gavel,
  DeviceMobile,
  ChartLineUp,
  Bank,
  Newspaper,
  Star,
} from "@phosphor-icons/react";

const FALLBACK_RECENT = [
  { icon: "Sparkle", title: "Loan Rebalancer — AI repayment suggestions", body: "Answer 5 questions, pick an AI model, get a repayment plan. Estima's calculator rechecks every figure, lock-in and charge before you see it.", link: "/app/loans" },
  { icon: "ChartLineUp", title: "Loan Rebalancer — custom charts & reports", body: "Progress & Reports tab: paid vs remaining as on any date, line/area/bar/combo/donut charts, PDF + Excel payment reports.", link: "/app/loans" },
  { icon: "Bank", title: "Loan Rebalancer — bulk upload, bonus, prepay, rate changes", body: "Upload up to 25 loans from Excel, split a bonus, simulate prepayments, and recalculate after an RBI or bank rate change.", link: "/app/loans" },
  { icon: "Newspaper", title: "Property news", body: "Curated headlines from Indian real-estate desks — each line summarised by AI with a Positive / Neutral / Watch-out tag.", link: "/app/news" },
  { icon: "Calculator", title: "Rental Snowball — builder plans", body: "Builder schemes 10:90, 20:80, 30:70, 40:60, 50:50, Subvention, RTM. Toggle who pays pre-EMI; XIRR updates.", link: "/app/calculators" },
  { icon: "MapPin", title: "Rent comps — society + date filters", body: "Capture society/area/property-type. Filter by date range to track how rents in your pocket move over time.", link: "/app/rent-comps" },
];

function ResolveIcon(name) {
  const I = Phosphor[name];
  return I || Sparkle;
}

// Most-recent features get top-billing on the Guide page.
const _LEGACY_RECENT_UPDATES_REMOVED = []; // replaced by API-driven feed below

const FEATURES = [
  {
    icon: Bank,
    title: "Loan Rebalancer — many loans, one EMI view",
    link: "/app/loans",
    body:
      "Add unlimited loans and overdrafts (with dropline years) and see your true monthly outflow. Seven tabs: My Loans · Bonus Advisor · Prepayment Simulator · Rate Changes · Saved Scenarios · Progress & Reports · AI Suggestions.",
    tips: [
      "Bulk upload: download the Excel template, fill in up to 25 loans (.xlsx or .csv, max 2 MB), upload, review the preview and confirm.",
      "Bonus Advisor: enter a bonus or windfall and Estima shows how to split it across your loans for the biggest saving, respecting lock-ins and prepayment charges.",
      "Prepayment Simulator: try one-time or recurring part-payments on any loan, compare reduce-tenure vs reduce-EMI, and save up to 10 scenarios.",
      "Rate Changes: update one loan's rate or apply an RBI repo change to all linked loans. Choose Keep EMI, Keep tenure or a custom EMI; every change is logged with Undo. Saved scenarios on that loan are flagged as out of date.",
      "Progress & Reports: paid vs remaining for each loan or all loans, as on any date, grouped monthly, by calendar year or financial year. Download a payment report as PDF or Excel.",
      "Chart options: line, area, stacked, grouped, combo or donut; individual or combined loans; principal/interest split; per period or running total; rupees or % of EMI; past, future or full life. Your choices are remembered.",
      "AI Suggestions: give consent once, answer 5 questions (money available, extra monthly, goal, emergency fund, risk) and pick a model (Claude, GPT, Gemini or Sarvam). Estima's calculator recalculates every figure and drops any step that breaks a lock-in or exceeds your money. 5 suggestions a day; history is saved and you can withdraw consent anytime.",
      "Each row captures: name, kind (loan or overdraft), interest rate, initial sanctioned amount, tenure (or dropline years for OD), current outstanding, optional part-payment amount.",
      "Reduce-tenure strategy keeps EMI flat and ends the loan earlier — it almost always beats Reduce-EMI for total interest saved.",
      "Reduce-EMI strategy keeps tenure flat and frees up monthly cashflow — useful right before a major life event (kid's college, sabbatical).",
      "Overdrafts: set 'Dropline (years)' so Estima models the linear principal reduction. Part-payments on an OD reduce the effective monthly EMI.",
      "Save your plan with a name — it shows up in the Saved plans bar at the top on every visit. Multiple plans (e.g. \"Aggressive prepay\" vs \"Conservative\") are supported.",
      "Export to PDF for your CA or to Excel for your own scenario building. Both files include the disclaimer.",
      "AI tip button → Claude returns 3 paragraphs: which loan first, recommended strategy, and risk caveats. Treat as guidance, not regulated advice.",
    ],
  },
  {
    icon: Newspaper,
    title: "Property news — AI-summarised headlines",
    link: "/app/news",
    body:
      "Headlines from Indian real-estate desks (ET Realty, Moneycontrol, ET Realty Policy) summarised by Claude in one plain-English sentence + a Positive / Neutral / Watch-out tag from a homebuyer & landlord lens.",
    tips: [
      "Filter by tone (Positive / Neutral / Watch-out) when you only have a minute.",
      "Filter by topic (Market vs Policy / RBI) to focus on rate-cut and rule-change news during a buying decision.",
      "Click any card to read the original article — Estima never paywalls or rewrites the source.",
      "Headlines refresh on a regular cadence; what you see is the latest cached snapshot.",
    ],
  },
  {
    icon: Buildings,
    title: "Properties ledger · Nearby market · 1-click listings",
    link: "/app/properties",
    body:
      "Add every flat, villa or plot you're evaluating — plus any you already own or have sold. Each property carries price, carpet area, loan terms, your subjective scores (location/amenities/safety/commute/resale), and optional owned/sold metadata. Owned properties also surface nearby rent/sqft data and a 1-click rental-listing generator.",
    tips: [
      "Use the Apply city preset row to auto-fill appreciation & yield for Bengaluru, Mumbai, Hyderabad etc.",
      "Status toggle: Evaluating (default), Owned (tracks current value, equity, rent), Sold (records gain/loss).",
      "Nearby market card on owned properties: see community-contributed rent ₹/sqft around your locality; contribute your own data point in one tap.",
      "\"List for rent in 1 click\" on owned cards — pick rent/deposit/furnishing, optionally ask AI to write the description, and Estima returns paste-ready deep-links for 99acres, MagicBricks, Housing.com, NoBroker, OLX, Quikr, plus a WhatsApp share text.",
      "Filter tabs at the top of the Properties page: All / Evaluating / Owned / Sold.",
    ],
  },
  {
    icon: Scales,
    title: "Side-by-side Compare",
    link: "/app/compare",
    body:
      "Pick two or more properties in Compare, tune the six weights (location, amenities, safety, commute, resale, price/value), and Estima returns a weighted winner plus a radar breakdown and ranked table.",
    tips: [
      "Each weight is independent — they don't need to sum to 100%.",
      "Export the report as CSV or PDF (Pro). Share a read-only link with anyone — no login required on their side.",
      "Price/Value is auto-computed: the cheapest ₹/sqft scores highest.",
    ],
  },
  {
    icon: Calculator,
    title: "15+ Calculators with proper XIRR",
    link: "/app/calculators",
    body:
      "Every number real-estate agents never show you — now with proper XIRR, leverage-adjusted ROI, long-horizon wealth modelling, and AI-assisted summaries on every tab.",
    tips: [
      "Why buy (wealth) — 25-year buy vs rent-invest narrative with tier-1 premium + generational-transfer bonus.",
      "Car vs Property — full finance comparison (car DP + loan + running cost vs property DP + loan + rent).",
      "EMI · Rent vs Buy · Property vs MF vs Equity · Cashflow-positive finder.",
      "Rent-for-cashflow — the rent you need today to make your owned property CF-positive in N years.",
      "Rental Snowball (OD) — multi-flat simulator with builder payment plans (10:90, 20:80, 30:70, 40:60, 50:50, Subvention, RTM) and a 'Pre-EMI paid by Self / Builder' toggle. Shows exactly when the portfolio self-funds. XIRR on all DPs + Claude-generated 3-paragraph buyer's letter.",
      "Resale estimator — min/current rent, renovation, broker fee, LTCG. Returns true XIRR.",
      "Loan Leverage Optimizer — DP sweep with 10-yr XIRR including appreciation + rent + EMI.",
      "Builder plan — CLP vs 10:90 vs subvention. UC expected value at possession (w/ pre-EMI by builder).",
      "RTM vs UC breakeven — min DP for each + 5-yr XIRR + subvention support.",
      "Prepay vs Invest — part / full / invest-surplus / hybrid scenarios with winner detection.",
      "XIRR — proper Newton-Raphson IRR for irregular real-estate cashflows.",
      "Zoom-in / Zoom-out and AI-summary on every tab — the magnifying icons sit just above each calculator.",
    ],
  },
  {
    icon: ChartPieSlice,
    title: "Portfolio",
    link: "/app/portfolio",
    body:
      "For properties you already own or have sold. Net worth, current value, total equity, realized gains, monthly cashflow; timeline chart; vs-markets comparison against equity/MF/gold/silver/FD; Sold ledger with realized P&L.",
    tips: [
      "Mark a property as Sold with sold_date + sold_price to see realized gains show up as a step line on the timeline.",
      "The opportunity-cost chart uses historical CAGR defaults (Equity 13%, MF 11%, Gold 9%, Silver 8.5%, FD 7%) — you'll be surprised how often your property wins.",
      "Net worth = current equity + cumulative realized gains from sold properties.",
    ],
  },
  {
    icon: MapTrifold,
    title: "Upcoming Projects directory",
    link: "/app/projects",
    body:
      "Curated under-construction & upcoming launches across tier-1 cities (BLR / MUM / NCR / HYD / PUN / CHN / KOL / AMD) with builder, possession, ticket size, RERA, and amenities. Filter by city, area, status. Star-watch projects and they show on your Dashboard.",
    tips: [
      "Submit your own community projects — they appear alongside curated ones.",
      "Watched projects show up as a section on your Dashboard with quick-access cards.",
    ],
  },
  {
    icon: UserCircle,
    title: "Tenants + Rent portal",
    link: "/app/tenants",
    body:
      "Track tenants across your owned rentals — name, rent, email, phone. Generate PDF rent receipts instantly. Invite your tenant to their own portal: they log in at /login and auto-route to /tenant, where they can pay rent via Razorpay and download receipts.",
    tips: [
      "Click \"Invite to tenant portal\" on any tenant card (needs the tenant's email). Estima creates a login with a temp password and shows it to you.",
      "Online rent payment auto-generates a receipt with payment_mode='Razorpay' and a unique receipt number.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Aadhaar tenant verification",
    link: "/app/tenants",
    body:
      "Verify your tenant's Aadhaar on-file using a stubbed OTP flow (wired to UIDAI's official sandbox contract for easy prod-swap). Once verified, the card shows a \"Aadhaar verified ****XXXX\" badge — useful for rental agreements and society onboarding.",
    tips: [
      "Click Verify Aadhaar on any tenant card → enter last-4 digits + Aadhaar-linked phone → send OTP → confirm.",
      "The stub accepts any 6-digit OTP; we plan to swap this for a real partner integration shortly.",
    ],
  },
  {
    icon: MapPin,
    title: "Rent comps — community-sourced going rates",
    link: "/app/rent-comps",
    body:
      "Search any 6-digit pincode and see real rents from other Estima users — median, 25th & 75th percentile, ₹/sqft, and recent samples. Submit your own rent in 30 seconds and the dataset gets sharper for everyone.",
    tips: [
      "Submissions capture pincode, BHK, area, rent, society name, area name, and apartment-vs-individual-house. No names, no addresses.",
      "Search filters: 6-digit pincode + property type (apartment / individual house) + From / To date range — useful to track how rents have moved over the last 3, 6, 12 months.",
      "Owned properties on the Properties page can auto-contribute their current rent in 1 click.",
      "Use the comps to pressure-test the rent in your Snowball plan or the assumed yield in city presets.",
    ],
  },
  {
    icon: Scroll,
    title: "Will — AI draft · Counsel marketplace · Witness e-sign · Notify",
    link: "/app/will",
    body:
      "A full succession workflow. Draft your Last Will and Testament with property-level allocations across named beneficiaries — or let AI draft a distribution based on your family context. Book a verified lawyer from Estima's panel (Razorpay payment) or bring your own, collect Aadhaar-backed e-signatures from witnesses, and notify each beneficiary with a password-protected PDF.",
    tips: [
      "AI-draft — provide family context + distribution style (equal / spouse-first / legacy-trust / custom) and Claude Sonnet 4.5 returns beneficiaries + per-property splits + reasoning. You can edit anything before saving.",
      "Ask Estima Counsel — click Browse Estima Counsel to pick a verified family-law advocate, pay their fee via Razorpay, and they review + endorse your Will within 30 days. Transparent pricing.",
      "Bring your own lawyer — prefer a lawyer you already trust? Send them a private review link via email (no fee, no platform involvement).",
      "Witness e-sign — invite witness 1 and witness 2 via email. They sign at a public link with Aadhaar last-4 + OTP.",
      "Notify beneficiaries — single click emails each beneficiary a password-protected Will PDF.",
      "Download an Indian-Succession-Act-compliant PDF at any point.",
    ],
  },
  {
    icon: Gavel,
    title: "Estima Counsel — for lawyers",
    link: "/lawyer/register",
    body:
      "Are you a family-law counsel? Join Estima's review marketplace. Set your fee per review, receive AI-drafted Wills + context from clients, endorse them, and earn. We handle payments + notifications.",
    tips: [
      "Self-signup at /lawyer/register with Bar Council ID, specialization, rate and bio. You're live to clients once Estima verifies your profile.",
      "Dashboard shows your queue (pending + completed), earnings total, endorsement count, and lets you update your rate/bio anytime.",
      "Every completed review can be 'endorsed' — endorsed lawyers rank higher in the client-facing list.",
    ],
  },
  {
    icon: Sparkle,
    title: "AI Advisor",
    link: "/app/advisor",
    body:
      "A Claude Sonnet 4.5 session that sees your property list and answers grounded questions. Pro feature.",
    tips: [
      "Ask specific questions: \"Given my 3 Bengaluru flats, which yields the best 10-year return assuming a job move to Chennai in 2028?\"",
      "The advisor always returns a 2-line verdict, 3-5 bullet reasoning, and one concrete next step.",
      "Session memory persists across messages. Click the seed prompts to see example queries.",
    ],
  },
  {
    icon: Gift,
    title: "Refer & earn · Demo mode",
    link: "/app/referrals",
    body:
      "Every account gets a unique referral code — each signup via your link earns you 30 extra days of Pro, instantly. Prefer to browse first? Click \"Try the demo — no signup\" on the login page for a fully-seeded sandbox (4 sample properties, tenants, and receipts).",
    tips: [
      "Copy or WhatsApp-share your code from the Referrals page. Earnings credit the moment the referred user verifies their email.",
      "Demo account is isolated — your real data is never touched. Great for sharing screenshots with a prospect.",
    ],
  },
  {
    icon: Crown,
    title: "Pricing & Pro",
    link: "/pricing",
    body:
      "Free ₹0 · Basic ₹499/month · Pro ₹999/year. Every new account starts with a {TRIAL} free trial automatically (Basic features). Or click \"Try the demo\" on the login page — no signup.",
    tips: [
      "Pro unlocks unlimited properties, AI advisor, CSV/PDF exports, share links, cashflow finder, vs-markets chart, portfolio timeline, AI Will drafting, Will PDF, and Loan Rebalancer exports.",
      "Cancel anytime — your Pro features keep working until the end of the paid period.",
    ],
  },
  {
    icon: DeviceMobile,
    title: "Sign in with phone (OTP) — install as an app",
    link: "/login",
    body:
      "Estima is mobile-first. One-tap install on iPhone/Android (PWA), bottom tab bar for the 4 most-used features, slide-in drawer for the rest. Sign in with email, Google, or just your phone number — Firebase OTP, India +91, no password needed.",
    tips: [
      "Phone OTP login: tap 'Continue with Phone' on /login → enter +91 number → 6-digit SMS OTP → 1-step profile (name, optional email) → you're in.",
      "Add to Home Screen: open Estima in Chrome (Android) or Safari (iPhone) → install banner appears → tap Install. Opens fullscreen, no browser chrome.",
      "Bottom tab bar (mobile only): Home / Calc / Compare / Advisor / More. Drawer accessible from the More tab or the top-left hamburger.",
      "If phone login ever misbehaves on a previously-installed app, visit /debug/firebase and tap 'Unregister Service Worker + clear caches' — that resets the app shell instantly.",
    ],
  },
  {
    icon: ChartLineUp,
    title: "Public share links + dashboard activity",
    link: "/app",
    body:
      "Every Snowball, OD-cashflow, and property comparison generates a signed public link at /share/{id} — recipients see a clean read-only report (no login). Your dashboard now shows the last 10 things you did + a 'My shared reports' panel with WhatsApp / Copy / Delete actions per row.",
    tips: [
      "Snowball share pages include the Claude AI buyer's letter — recipients get the math AND the persuasion in one place.",
      "Recent activity widget surfaces unfinished work (added a property → never compared it? Created a snowball → forgot to share?).",
      "Every share = a free acquisition channel: each visit nudges the recipient toward signup.",
    ],
  },
  // Admin section is gated by `adminOnly` and only renders for users with admin role.
  // Kept here intentionally — non-admin users never see it.
  {
    icon: ShieldCheck,
    title: "Admin tools (Pro / Org accounts)",
    link: "/app/admin",
    adminOnly: true,
    body:
      "If you're an Estima admin, you have a private console: full footfall analytics, route-level pageviews, top features, daily trends. A read-only Data Viewer (with default PII masking + audit-logged Reveal toggle + masked CSV export). A Portfolio Analytics tab — aggregate property data across all users with charts and CSV export, PII masked by default. Reply to any user feedback inline. An Email Test harness to fire any of the 11 templates against any address for spot-check / deliverability.",
    tips: [
      "Data Viewer masks every PII field by default. Reveal requires a confirm dialog AND writes to admin_audit.",
      "CSV exports respect the same masking — use Reveal first if you need raw values for an analyst (audit-logged).",
      "Email harness sends real Resend emails — visible at resend.com/emails with delivery + open rates.",
    ],
  },
];

const FAQS = [
  {
    q: "How is the decision score calculated?",
    a: "For each property we take your 0-10 scores on Location/Amenities/Safety/Commute/Resale and multiply by your weights. Price/Value is auto-computed: the cheapest property per sqft gets 10, the most expensive gets 1, and the rest scale linearly. Total score is the weighted sum.",
  },
  {
    q: "How does the Loan Rebalancer decide if I should reduce tenure or reduce EMI?",
    a: "Reduce-tenure keeps your EMI the same but ends the loan earlier — you save the maximum interest because every rupee of part-payment immediately stops accruing future interest for the rest of the original tenure. Reduce-EMI keeps the months the same and lowers your monthly outflow, freeing cash but saving less interest. As a rule of thumb: pick Reduce-tenure unless you specifically need the monthly cashflow back. The AI Suggestions tab on the Loan Rebalancer reads your loans and goal and tells you which strategy fits your numbers.",
  },
  {
    q: "How do I upload all my loans at once?",
    a: "In the Loan Rebalancer, tap Bulk upload, download the Excel template, fill one row per loan (up to 25 loans, .xlsx or .csv, max 2 MB) and upload it. You'll see a preview with any errors highlighted before anything is saved.",
  },
  {
    q: "What happens to my loans when RBI changes the repo rate?",
    a: "Open Rate Changes and choose 'RBI changed the repo rate'. Estima applies the change to your repo-linked loans and shows, per loan, what happens if you keep your EMI or keep your tenure (or set a custom EMI). Each change is saved in a history with Undo, and any saved prepayment scenario on that loan is marked out of date.",
  },
  {
    q: "Can I see how much of my loan I've already paid?",
    a: "Yes — the Progress & Reports tab shows principal and interest paid and remaining for each loan or all loans together, as on any date. Customise the chart (line, area, bar, combo, donut; monthly, calendar or financial year) and download a payment report as PDF or Excel.",
  },
  {
    q: "Are the AI loan suggestions accurate?",
    a: "The AI only suggests a strategy. Estima's own loan calculator then recalculates every number — interest saved, EMI freed, debt-free date — and removes any step that breaks a lock-in, a minimum prepayment or your available money. It is guidance, not regulated financial advice. You get 5 suggestions a day, and you can withdraw your AI consent at any time.",
  },
  {
    q: "How does the Rental Snowball treat a builder subvention scheme?",
    a: "Pick the 'Subvention (builder pays pre-EMI)' option in the per-flat 'Builder plan' dropdown — Estima locks the 'Pre-EMI paid by' toggle to Builder and zeroes your construction-window outflows. Interest still accrues on the loan internally, but it does not hit the OD pot or your XIRR cashflow series. The result mirrors what you'd actually pay to the bank for that flat.",
  },
  {
    q: "Where do the default market returns come from?",
    a: "We use long-run CAGR approximations commonly cited in Indian personal finance: Equity (Nifty) ~13%, Mutual Funds ~11%, Gold ~9%, Silver ~8.5%, Bank FDs ~7%. These are defaults — override them on the Portfolio vs-markets section if your assumptions differ.",
  },
  {
    q: "How fresh is the news on /app/news?",
    a: "Headlines are pulled from ET Realty, Moneycontrol Real Estate and ET Realty Policy on a regular cadence. Each item is summarised in one plain-English sentence by Claude AI and tagged Positive / Neutral / Watch-out from a homebuyer's lens. Click any card to read the original article on the source publication.",
  },
  {
    q: "Is my data private?",
    a: "Yes. Every property, calculation, advisor conversation, and saved loan plan is scoped to your account. Shared comparison/snowball/OD links are opt-in snapshots — the shared version never reveals your email or the other properties in your ledger.",
  },
  {
    q: "How do I sign in?",
    a: "Three ways: (1) email + password, (2) Google one-tap, (3) phone OTP — enter your +91 mobile, get a 6-digit SMS code via Firebase, you're in. The same account works across all three so you can switch any time.",
  },
  {
    q: "Phone login throws 'auth/network-request-failed' — what now?",
    a: "Almost always a stale installed-PWA cache. Open /debug/firebase in your browser, tap 'Unregister Service Worker + clear caches', then hard-reload (Ctrl+Shift+R or full app restart). If it still fails, the page prints exactly which Firebase / Google API check is failing — share that screen with us.",
  },
  {
    q: "Can I install Estima as an app?",
    a: "Yes — Estima is a PWA. On Android Chrome you'll see an Install banner; tap it. On iPhone Safari, tap Share → Add to Home Screen. The installed app opens fullscreen, has its own icon, and works offline for the basic shell.",
  },
  {
    q: "Can I save multiple loan plans?",
    a: "Yes — in the Loan Rebalancer, give each plan a name (e.g. 'Aggressive prepay' vs 'Conservative') and tap Save. Saved plans appear in a chip-bar at the top of the page on every visit; one-click loads them, one-click deletes them. They're scoped to your account, never shared.",
  },
  {
    q: "Can I export my Loan Rebalancer plan to send to my CA?",
    a: "Yes. The Loan Rebalancer has Export PDF and Export Excel buttons in the result panel. PDF is a 1-page summary with the disclaimer; Excel includes two sheets — Summary and Loans — for your own scenario building.",
  },
  {
    q: "Can I import properties from a CSV?",
    a: "Not yet — it's on the roadmap. For now the fastest path is to use a city preset + sensible defaults for rows you haven't decided on yet; Estima treats any field as editable.",
  },
  {
    q: "What happens when my trial ends?",
    a: "Your account reverts to the Free plan. Your data stays intact. AI features and PDF/Excel exports lock, but all calculators, Compare, Portfolio tracking (read-only), saved loan plans, and your saved properties remain accessible.",
  },
  {
    q: "How do I get a refund?",
    a: "Razorpay refunds are supported within 14 days — email us from your registered email and we'll process it.",
  },
  {
    q: "Is the Aadhaar tenant verification real?",
    a: "The Aadhaar verification flow is currently a stub (accepts any 6-digit OTP) so the whole product works end-to-end. The flow matches how official partners expose Aadhaar OTP, and we'll swap it to live verification shortly.",
  },
  {
    q: "How accurate is the Nearby market data and the Rent comps?",
    a: "Two sources: (1) Estima's curated city-rent presets per locality, and (2) anonymous user contributions. Median and mean are reported separately so outliers are visible. Always sanity-check against 99acres / MagicBricks — and please contribute a data point after you've rented out your own flat. The Rent comps page now also lets you filter by date range, so you can see how rents in your pocket have moved over the last 3, 6 or 12 months.",
  },
  {
    q: "Can the AI actually draft my Will?",
    a: "AI-draft takes your properties + family context + distribution style (equal / spouse-first / legacy-trust / custom) and returns beneficiaries + per-property allocations + reasoning. It's a starting draft, not legal advice — every output is fully editable before saving, and you should still run it past a family-law lawyer before signing.",
  },
];

export default function Guide() {
  const { user } = useAuth();
  const [showTour, setShowTour] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [trialLabel, setTrialLabel] = useState("6-month");

  useEffect(() => {
    api.get("/billing/pricing")
      .then(({ data }) => setTrialLabel(formatTrialDuration(data?.trial_days || 180)))
      .catch(() => {});
  }, []);

  // Substitute {TRIAL} token in any feature/FAQ string with the live duration.
  const fillTrial = (s) => (typeof s === "string" ? s.replace(/\{TRIAL\}/g, trialLabel) : s);

  // Memoise visible features so we don't re-filter on every render.
  const visibleFeatures = useMemo(
    () => FEATURES.filter((f) => !f.adminOnly || user?.role === "admin"),
    [user?.role]
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-10" data-testid="guide-page">
      <Tour open={showTour} onClose={() => setShowTour(false)} />

      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">User guide</div>
          <h1 className="font-serif text-3xl sm:text-5xl">How to use Estima.</h1>
          <p className="text-muted-foreground mt-3 max-w-xl">
            A 90-second tour, the latest updates, full feature reference, and the answers to the questions
            every new user asks.
          </p>
        </div>
        <button
          onClick={() => {
            resetTour();
            setShowTour(true);
          }}
          className="btn-primary px-5 py-3 inline-flex items-center gap-2 text-sm"
          data-testid="guide-run-tour"
        >
          <PlayCircle size={16} weight="duotone" /> Run the tour again
        </button>
      </div>

      {/* Quick cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
        {[
          ["Properties", Buildings, "/app/properties"],
          ["Portfolio", ChartPieSlice, "/app/portfolio"],
          ["Compare", Scales, "/app/compare"],
          ["Calculators", Calculator, "/app/calculators"],
          ["Loan rebalancer", Bank, "/app/loans"],
          ["News", Newspaper, "/app/news"],
          ["Rent comps", MapPin, "/app/rent-comps"],
          ["Projects", MapTrifold, "/app/projects"],
          ["Tenants", UserCircle, "/app/tenants"],
          ["Will", Scroll, "/app/will"],
          ["AI Advisor", Sparkle, "/app/advisor"],
          ["Refer & earn", Gift, "/app/referrals"],
        ].map(([label, Icon, to]) => (
          <Link
            key={label}
            to={to}
            className="card-flat p-5 hover:border-[hsl(var(--secondary))] transition flex items-center gap-4"
            data-testid={`guide-quick-${label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="h-10 w-10 border hairline flex items-center justify-center text-[hsl(var(--secondary))] shrink-0">
              <Icon size={18} weight="duotone" />
            </div>
            <div>
              <div className="font-serif text-lg">{label}</div>
              <div className="text-xs text-muted-foreground">Go to {label.toLowerCase()} →</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent updates — API-driven; falls back to a static snapshot if unauth/offline */}
      <RecentUpdatesSection />

      {/* Feature deep-dives */}
      <section className="mb-16">
        <div className="eyebrow mb-4">Feature reference</div>
        <div className="space-y-px bg-[hsl(var(--border))] border hairline">
          {visibleFeatures.map((rawF, i) => {
            const f = { ...rawF, body: fillTrial(rawF.body), tips: (rawF.tips || []).map(fillTrial) };
            const Icon = f.icon;
            return (
              <div key={f.title} className="bg-card p-8" data-testid={`guide-feature-${i}`}>
                <div className="flex items-start gap-5">
                  <div className="h-12 w-12 border hairline flex items-center justify-center text-[hsl(var(--secondary))] shrink-0">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-serif text-2xl">{f.title}</h3>
                      <Link to={f.link} className="text-xs text-muted-foreground hover:text-foreground">
                        Open →
                      </Link>
                    </div>
                    <p className="text-muted-foreground leading-relaxed mb-4">{f.body}</p>
                    <ul className="space-y-2">
                      {f.tips.map((t) => (
                        <li key={t} className="flex gap-2 text-sm">
                          <span className="text-[hsl(var(--secondary))] font-serif">·</span>
                          <span className="text-muted-foreground">{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQs */}
      <section className="mb-16">
        <div className="flex items-center gap-3 mb-4">
          <Question size={18} weight="duotone" className="text-[hsl(var(--secondary))]" />
          <div className="eyebrow">Frequently asked</div>
        </div>
        <div className="card-flat divide-y divide-[hsl(var(--border))]">
          {FAQS.map((f, i) => {
            const open = openFaq === i;
            return (
              <button
                key={f.q}
                onClick={() => setOpenFaq(open ? null : i)}
                className="w-full text-left p-5 hover:bg-[hsl(var(--muted))] transition"
                data-testid={`faq-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="font-serif text-lg">{f.q}</div>
                  <CaretDown
                    size={16}
                    className={`mt-1 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
                  />
                </div>
                {open && (
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Shortcuts */}
      <section>
        <div className="eyebrow mb-4">Pro tips</div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="card-flat p-6">
            <FilePdf size={20} weight="duotone" className="text-[hsl(var(--secondary))] mb-3" />
            <div className="font-serif text-xl mb-2">Export the decision</div>
            <p className="text-sm text-muted-foreground">
              After scoring in Compare, use Export PDF to get a clean 1-page report. The Loan Rebalancer
              also exports to PDF and Excel — share with your spouse, CA, or parents instead of screenshotting spreadsheets.
            </p>
          </div>
          <div className="card-flat p-6">
            <ShareNetwork size={20} weight="duotone" className="text-[hsl(var(--secondary))] mb-3" />
            <div className="font-serif text-xl mb-2">Share any scenario (privately)</div>
            <p className="text-sm text-muted-foreground">
              Comparison reports, OD-cashflow scenarios AND Rental Snowball plans all generate signed public links at
              {" "}
              <code className="text-foreground">/share/&lt;id&gt;</code>. Every share is a snapshot — future edits
              don't change what the recipient sees. Your Dashboard lists every report with one-click WhatsApp / Copy / Delete.
            </p>
          </div>
          <div className="card-flat p-6">
            <Bank size={20} weight="duotone" className="text-[hsl(var(--secondary))] mb-3" />
            <div className="font-serif text-xl mb-2">Save loan plans for later</div>
            <p className="text-sm text-muted-foreground">
              In the Loan Rebalancer, name and Save your plan. Try a few — "Aggressive prepay", "Conservative",
              "After bonus" — and load them back in one tap on every visit.
            </p>
          </div>
          <div className="card-flat p-6">
            <EnvelopeSimple size={20} weight="duotone" className="text-[hsl(var(--secondary))] mb-3" />
            <div className="font-serif text-xl mb-2">Get the AI's view, then decide</div>
            <p className="text-sm text-muted-foreground">
              Every Snowball plan, calculator tab, and Loan Rebalancer scenario has a Claude-powered AI summary —
              treat it as a second opinion, not a verdict. The disclaimers spell out the limits.
            </p>
          </div>
        </div>
      </section>

      {user && !user.is_pro && (
        <div className="mt-16 card-flat p-8 border-[hsl(var(--secondary))] text-center">
          <Crown size={28} weight="duotone" className="text-[hsl(var(--secondary))] mx-auto mb-3" />
          <div className="font-serif text-3xl mb-3">Still on Free?</div>
          <p className="text-muted-foreground mb-6">
            Unlock the AI advisor, exports, share links, cashflow finder and vs-markets for ₹999/mo or ₹9,999/yr.
          </p>
          <Link to="/pricing" className="btn-primary px-6 py-3 text-sm inline-flex items-center gap-2" data-testid="guide-upgrade-cta">
            See plans
          </Link>
        </div>
      )}
    </div>
  );
}

function RecentUpdatesSection() {
  const [items, setItems] = useState(null); // null = loading
  useEffect(() => {
    let alive = true;
    api.get("/public/product-updates?limit=8")
      .then(({ data }) => {
        if (!alive) return;
        const list = Array.isArray(data) && data.length ? data : FALLBACK_RECENT;
        setItems(list);
      })
      .catch(() => { if (alive) setItems(FALLBACK_RECENT); });
    return () => { alive = false; };
  }, []);

  if (items === null) {
    return (
      <section className="mb-16" data-testid="guide-recent">
        <div className="flex items-center gap-3 mb-4">
          <Star size={18} weight="duotone" className="text-[hsl(var(--secondary))]" />
          <div className="eyebrow">Recently added</div>
        </div>
        <div className="card-flat p-8 text-center text-sm text-muted-foreground">Loading…</div>
      </section>
    );
  }

  if (!items.length) return null;

  return (
    <section className="mb-16" data-testid="guide-recent">
      <div className="flex items-center gap-3 mb-4">
        <Star size={18} weight="duotone" className="text-[hsl(var(--secondary))]" />
        <div className="eyebrow">Recently added</div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((u, i) => {
          const Icon = ResolveIcon(u.icon || "Sparkle");
          const target = u.link || "/app";
          const isExternal = /^https?:\/\//.test(target);
          const className = "card-flat p-5 hover:border-[hsl(var(--secondary))] transition block";
          const inner = (
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 border hairline flex items-center justify-center text-[hsl(var(--secondary))] shrink-0">
                <Icon size={18} weight="duotone" />
              </div>
              <div className="flex-1">
                <div className="font-serif text-xl mb-1 flex items-center gap-2">
                  {u.title}
                  {u.pinned && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[hsl(var(--secondary))]/60 text-[hsl(var(--secondary))]">Pinned</span>}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{u.body}</p>
              </div>
            </div>
          );
          return isExternal ? (
            <a key={u.id || u.title} href={target} target="_blank" rel="noreferrer" className={className} data-testid={`guide-recent-${i}`}>
              {inner}
            </a>
          ) : (
            <Link key={u.id || u.title} to={target} className={className} data-testid={`guide-recent-${i}`}>
              {inner}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

